import type {
  TradingAppAd,
  TradingAppMergedPosition,
  TradingAppPlatform,
  TradingAppRecommendation,
} from '~/types/homeTradingApps'

export const IOS_PLATFORM = 1 as const
export const ANDROID_PLATFORM = 2 as const
export const TRADING_APP_COLUMN_COUNT = 3
export const TRADING_APPS_PER_COLUMN = 3
export const TRADING_APP_MAX_VISIBLE_ITEMS = TRADING_APP_COLUMN_COUNT * TRADING_APPS_PER_COLUMN

const WIKIBIT_STORE_ORIGIN = 'https://store.wikibit.com'

type UnknownRecord = Record<string, unknown>

/** 判断接口节点是否为可安全读取字段的普通对象。 */
const isRecord = (value: unknown): value is UnknownRecord => {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

/** 从不同版本接口字段中读取并清理文本值。 */
const readText = (...values: unknown[]): string => {
  const value = values.find(
    (item) => (typeof item === 'string' && Boolean(item.trim())) || (typeof item === 'number' && Number.isFinite(item)),
  )
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number' && Number.isFinite(value)) return value.toString()
  return ''
}

/** 读取接口中可作为业务数字/标识的原始字符串或数字值。 */
const readStringOrNumber = (...values: unknown[]): string | number | undefined => {
  const value = values.find(
    (item) => (typeof item === 'string' && Boolean(item.trim())) || (typeof item === 'number' && Number.isFinite(item)),
  )
  if (typeof value === 'number') return value
  if (typeof value === 'string') return value.trim()
  return undefined
}

/** 兼容截图数组、JSON 字符串和单个地址三种返回格式。 */
const normalizeScreenshots = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .flatMap((item) => (Array.isArray(item) ? normalizeScreenshots(item) : [item]))
      .filter((item): item is string => typeof item === 'string' && Boolean(item.trim()))
      .map((item) => item.trim())
  }

  if (typeof value !== 'string' || !value.trim()) {
    return []
  }

  try {
    const parsed = JSON.parse(value) as unknown
    if (Array.isArray(parsed)) {
      return normalizeScreenshots(parsed)
    }
  } catch {
    // 非 JSON 文本按单个图片地址处理。
  }

  return [value.trim()]
}

/** 将接口中的单条应用归一为共享卡片使用的字段结构。 */
const normalizeTradingAppAd = (value: unknown): TradingAppAd | null => {
  if (!isRecord(value)) return null

  const appName = readText(value.appName, value.name, value.appTitle, value.title)
  const appLogo = readText(value.appLogo, value.logo, value.icon, value.iconUrl)
  const appBundleId = readText(value.appBundleId, value.bundleId, value.packageName)
  const traderCode = readText(value.traderCode, value.code)
  const adId = readText(value.adId, value.id)
  const softwareId = readText(value.softwareId)

  if (!appName && !appLogo && !appBundleId && !traderCode && !adId && !softwareId) {
    return null
  }

  return {
    adId,
    marketId: readStringOrNumber(value.marketId),
    appName,
    appTitle: readText(value.appTitle, value.title),
    appLogo,
    appBundleId,
    softwareId,
    traderCode,
    traderName: readText(value.traderName),
    traderIco: readText(value.traderIco, value.traderIcon),
    screenshots: normalizeScreenshots(
      value.screenshots ?? value.screenshot ?? value.screenShots ?? value.imageList ?? value.images,
    ),
    downloadNum: readStringOrNumber(value.downloadNum, value.downloadCount, value.downloads),
    sizes: readText(value.sizes, value.size),
  }
}

type RecommendationArraySearchResult = {
  found: boolean
  value: unknown[]
}

const RECOMMENDATION_CONTAINER_KEYS = [
  'result',
  'data',
  'advertisement$apirecommendationadlist',
  'advertisement',
  'apirecommendationadlist',
  'recommendation',
] as const

/** 判断响应节点是否明确报告业务失败。 */
const isBusinessFailure = (value: unknown): boolean => {
  if (!isRecord(value)) return false
  if (value.succeed === false || value.success === false) return true

  for (const field of ['code', 'httpCode', 'errorCode']) {
    const rawCode = value[field]
    if (rawCode === undefined || rawCode === null || rawCode === '') continue
    const code = Number(rawCode)
    if (Number.isFinite(code) && code !== 0 && code !== 200) return true
  }

  return false
}

/** 递归检查响应包装层，避免错误响应携带旧 result 时误渲染。 */
const containsBusinessFailure = (value: unknown, depth = 0): boolean => {
  if (depth > 8) return false
  if (typeof value === 'string' && value.trim()) {
    try {
      return containsBusinessFailure(JSON.parse(value) as unknown, depth + 1)
    } catch {
      return false
    }
  }
  if (!isRecord(value)) return false
  if (isBusinessFailure(value)) return true

  return RECOMMENDATION_CONTAINER_KEYS.some((key) => containsBusinessFailure(value[key], depth + 1))
}

/** 在 direct result 与 invoke-v3 嵌套响应中寻找最深层推荐模块数组。 */
const findRecommendationArray = (value: unknown, depth = 0): RecommendationArraySearchResult => {
  if (depth > 8) return { found: false, value: [] }
  if (Array.isArray(value)) return { found: true, value }

  // 某些代理会把整个响应序列化成 JSON 文本，先尝试解包再继续查找。
  if (typeof value === 'string' && value.trim()) {
    try {
      return findRecommendationArray(JSON.parse(value) as unknown, depth + 1)
    } catch {
      return { found: false, value: [] }
    }
  }

  if (!isRecord(value)) return { found: false, value: [] }

  let emptyResult: RecommendationArraySearchResult | null = null
  for (const key of RECOMMENDATION_CONTAINER_KEYS) {
    const result = findRecommendationArray(value[key], depth + 1)
    if (!result.found) continue
    if (result.value.length > 0) return result
    emptyResult ||= result
  }

  // 只有所有候选都为空时才返回第一个明确的空数组；不能让空 result 阻断后续 data。
  return emptyResult ?? { found: false, value: [] }
}

/** 判断对象是否具备足够字段，可视为单条推荐应用。 */
const isLikelyTradingAppAd = (value: UnknownRecord): boolean => {
  return Boolean(
    readText(
      value.adId,
      value.softwareId,
      value.appName,
      value.appLogo,
      value.logo,
      value.appBundleId,
      value.bundleId,
      value.traderCode,
    ),
  )
}

/** 判断模块数组中的对象是否携带推荐位元数据。 */
const isPositionRecord = (value: UnknownRecord): boolean => {
  return (
    'ads' in value ||
    'adList' in value ||
    'list' in value ||
    'items' in value ||
    'positionId' in value ||
    'positionName' in value ||
    'positionLogo' in value
  )
}

/** 判断推荐数组是否为旧接口直接返回的一维广告列表。 */
const isFlatTradingAppAdList = (value: readonly unknown[]): boolean => {
  if (value.some(Array.isArray)) return false

  const records = value.filter(isRecord)
  return records.some(isLikelyTradingAppAd) && !records.some(isPositionRecord)
}

/** 区分真实推荐位 ID 与归一化阶段按外层索引生成的占位 ID。 */
const isExplicitPositionId = (positionId: string, index: number): boolean => {
  return positionId !== `position-${index}`
}

/** 从模块中的 ads/list 嵌套字段递归收集应用节点。 */
const collectModuleAds = (value: unknown): unknown[] => {
  if (Array.isArray(value)) return value.flatMap((item) => collectModuleAds(item))
  if (!isRecord(value)) return []

  if (isLikelyTradingAppAd(value)) return [value]

  return ['ads', 'adList', 'list', 'items'].flatMap((key) =>
    Array.isArray(value[key]) ? collectModuleAds(value[key]) : [],
  )
}

/** 读取单个外层模块，并兼容模块直接是 ads 数组的二维返回格式。 */
const normalizeRecommendationModule = (value: unknown, positionIndex: number): TradingAppRecommendation | null => {
  if (Array.isArray(value)) {
    const records = value.filter(isRecord)
    const positionRecords = records.filter(isPositionRecord)
    const firstPosition = positionRecords[0]
    const adsSource = collectModuleAds(value)
    const ads = adsSource.map(normalizeTradingAppAd).filter((ad): ad is TradingAppAd => Boolean(ad))

    return {
      positionId: readText(firstPosition?.positionId, firstPosition?.id) || `position-${positionIndex}`,
      positionName: readText(firstPosition?.positionName, firstPosition?.title, firstPosition?.name),
      positionLogo: readText(firstPosition?.positionLogo, firstPosition?.logo),
      ads,
    }
  }

  if (!isRecord(value)) {
    return {
      positionId: `position-${positionIndex}`,
      positionName: '',
      ads: [],
    }
  }

  const ads = collectModuleAds(value)
    .map(normalizeTradingAppAd)
    .filter((ad): ad is TradingAppAd => Boolean(ad))

  return {
    positionId: readText(value.positionId, value.id) || `position-${positionIndex}`,
    positionName: readText(value.positionName, value.name, value.title),
    positionLogo: readText(value.positionLogo, value.logo),
    ads,
  }
}

/** 统一 direct 与 advertisement$apirecommendationadlist 的双层推荐数据。 */
export const normalizeTradingAppRecommendations = (response: unknown): TradingAppRecommendation[] => {
  if (containsBusinessFailure(response)) return []

  const { found, value: rawModules } = findRecommendationArray(response)
  if (!found) return []

  // 只折叠旧 direct 接口真正的一维 ad 列表；一旦含有数组元素，外层每项都代表一个推荐模块。
  if (isFlatTradingAppAdList(rawModules)) {
    const ads = rawModules.map(normalizeTradingAppAd).filter((ad): ad is TradingAppAd => Boolean(ad))
    return [{ positionId: 'position-0', positionName: '', ads }]
  }

  return rawModules
    .map((module, index) => normalizeRecommendationModule(module, index))
    .filter((module): module is TradingAppRecommendation => Boolean(module))
}

/**
 * 合并两个平台的推荐位。
 *
 * 有 positionId 时优先按 ID 合并；二维接口没有稳定 ID 时回退到外层索引，
 * 这样一侧缺少 ID 或返回空模块也不会让推荐位数量被意外拆分或折叠。
 */
export const mergeTradingAppRecommendations = (
  iosRecommendations: readonly TradingAppRecommendation[],
  androidRecommendations: readonly TradingAppRecommendation[],
): TradingAppMergedPosition[] => {
  type MergeEntry = {
    position: TradingAppMergedPosition
    sourceIndex: number
    explicitId: boolean
  }

  const entries: MergeEntry[] = []

  const getPositionId = (position: TradingAppRecommendation, index: number) => {
    const positionId = readText(position.positionId)
    return positionId || `position-${index}`
  }

  const append = (
    position: TradingAppRecommendation,
    platform: TradingAppPlatform,
    index: number,
    matchedEntries: Set<MergeEntry>,
  ) => {
    const positionId = getPositionId(position, index)
    const explicitId = isExplicitPositionId(positionId, index)
    const existing =
      (explicitId
        ? entries.find(
            (entry) => !matchedEntries.has(entry) && entry.explicitId && entry.position.positionId === positionId,
          )
        : undefined) ??
      entries.find(
        (entry) => !matchedEntries.has(entry) && entry.sourceIndex === index && (!entry.explicitId || !explicitId),
      )
    const ads = [...(position.ads ?? [])]

    if (existing) {
      existing.position.positionName ||= readText(position.positionName)
      existing.position.positionLogo ||= readText(position.positionLogo) || undefined

      if (platform === IOS_PLATFORM) {
        existing.position.iosAds = [...existing.position.iosAds, ...ads]
      } else {
        existing.position.androidAds = [...existing.position.androidAds, ...ads]
      }

      // If the index placeholder is paired with a real ID, promote the real ID
      // so subsequent updates and DOM keys remain stable.
      if (!existing.explicitId && explicitId) {
        existing.position.positionId = positionId
        existing.explicitId = true
      }
      matchedEntries.add(existing)
      return
    }

    const mergedPosition: TradingAppMergedPosition = {
      positionId,
      positionName: readText(position.positionName),
      positionLogo: readText(position.positionLogo) || undefined,
      iosAds: platform === IOS_PLATFORM ? ads : [],
      androidAds: platform === ANDROID_PLATFORM ? ads : [],
    }
    const entry: MergeEntry = { position: mergedPosition, sourceIndex: index, explicitId }
    entries.push(entry)
    matchedEntries.add(entry)
  }

  const matchedIosEntries = new Set<MergeEntry>()
  iosRecommendations.forEach((position, index) => append(position, IOS_PLATFORM, index, matchedIosEntries))
  const matchedAndroidEntries = new Set<MergeEntry>()
  androidRecommendations.forEach((position, index) =>
    append(position, ANDROID_PLATFORM, index, matchedAndroidEntries),
  )

  // Keep empty outer modules: the caller uses this array to preserve the API's
  // recommendation-module count, even when an individual slot has no ads.
  return entries.map(({ position }) => position)
}

/** 返回指定推荐位在目标平台下的应用列表。 */
export const getTradingAppAds = (position: TradingAppMergedPosition, platform: TradingAppPlatform): TradingAppAd[] => {
  return platform === ANDROID_PLATFORM ? position.androidAds : position.iosAds
}

/** 单个平台独占数据时优先该平台，否则采用设备偏好。 */
export const resolveTradingAppPositionPlatform = (
  position: TradingAppMergedPosition,
  preferredPlatform: TradingAppPlatform,
): TradingAppPlatform => {
  const hasIosApps = position.iosAds.length > 0
  const hasAndroidApps = position.androidAds.length > 0

  if (hasIosApps && !hasAndroidApps) {
    return IOS_PLATFORM
  }

  if (hasAndroidApps && !hasIosApps) {
    return ANDROID_PLATFORM
  }

  return preferredPlatform
}

/** 根据旧模块规则从 User-Agent 推导推荐应用的默认平台。 */
export const resolveTradingAppDefaultPlatform = (userAgent?: string): TradingAppPlatform => {
  const normalizedUserAgent = userAgent?.toLowerCase() || ''

  if (/macintosh|macintel|macppc|mac68k|mac os/u.test(normalizedUserAgent)) {
    return IOS_PLATFORM
  }

  if (/windows|win32|win64|winnt|linux|x11|android/u.test(normalizedUserAgent)) {
    return ANDROID_PLATFORM
  }

  if (/iphone|ipad|ipod/u.test(normalizedUserAgent)) {
    return IOS_PLATFORM
  }

  return IOS_PLATFORM
}

/** 最多展示九项，并按三列、每列三项进行纵向分组。 */
export const createTradingAppColumns = (ads: readonly TradingAppAd[]): TradingAppAd[][] => {
  const visibleAds = ads.slice(0, TRADING_APP_MAX_VISIBLE_ITEMS)

  return Array.from({ length: TRADING_APP_COLUMN_COUNT }, (_, columnIndex) => {
    const columnStart = columnIndex * TRADING_APPS_PER_COLUMN
    return visibleAds.slice(columnStart, columnStart + TRADING_APPS_PER_COLUMN)
  })
}

/** 构造 crypto-apps 详情地址，并保留当前语言前缀。 */
export const buildTradingAppDetailUrl = (ad: TradingAppAd, languageCode: string): string => {
  const marketId = String(ad.marketId ?? '').trim()
  const appBundleId = String(ad.appBundleId ?? '').trim()
  const traderCode = String(ad.traderCode ?? '').trim()

  if (!marketId || !appBundleId || !traderCode) {
    return ''
  }

  const normalizedLanguageCode = languageCode.trim().replace(/^\/+|\/+$/gu, '') || 'en'
  const softwareType = Number(marketId) === 11 ? 2 : 1
  const detailCode = [marketId, appBundleId, traderCode, softwareType].map(encodeURIComponent).join('-')

  return `${WIKIBIT_STORE_ORIGIN}/${encodeURIComponent(normalizedLanguageCode)}/crypto-apps/${detailCode}?fromType=1`
}

/** 交易商编码与品牌别名的映射，用于跳转交易所详情页。 */
export type TradingAppTraderAliasMap = Record<string, string>

/** 收集推荐数据中出现过的交易商编码，供批量解析品牌别名使用。 */
export const collectTradingAppTraderCodes = (recommendations: readonly TradingAppRecommendation[]): string[] => {
  const traderCodes = new Set<string>()

  for (const recommendation of recommendations) {
    for (const ad of recommendation.ads ?? []) {
      const traderCode = readText(ad.traderCode)
      if (traderCode) traderCodes.add(traderCode)
    }
  }

  return [...traderCodes]
}

/** 兼容品牌 URL 批量接口的直接数组与多层 data 包装，产出交易商编码到品牌别名的映射。 */
export const createTradingAppTraderAliasMap = (value: unknown): TradingAppTraderAliasMap => {
  let payload: unknown = value

  for (let depth = 0; depth < 4; depth += 1) {
    if (!Array.isArray(payload)) {
      if (!isRecord(payload) || !Object.prototype.hasOwnProperty.call(payload, 'data')) return {}
      payload = payload.data
      continue
    }

    const aliasMap: TradingAppTraderAliasMap = {}

    for (const item of payload) {
      if (!isRecord(item)) continue
      const traderCode = readText(item.traderCode, item.code)
      const alias = readText(item.alias)
      if (traderCode && alias) aliasMap[traderCode] = alias
    }

    return aliasMap
  }

  return {}
}
